import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Organization } from '../../types/organization';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Props {
  organization: Organization | null;
  isOpen: boolean;
  onClose: () => void;
  onDeleted: (org: Organization) => void;
}

const call = httpsCallable<{ organizationId: string; confirmName: string; mode: 'cascade' | 'orgOnly' }, { ok: boolean; counters: Record<string, number> }>(functions, 'adminDeleteOrganization');

/** Szervezet törlése névmegerősítéssel; alapból minden adatával együtt (Cloud Function, admin SDK). */
const DeleteOrganizationModal: React.FC<Props> = ({ organization, isOpen, onClose, onDeleted }) => {
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'cascade' | 'orgOnly'>('cascade');
  const [busy, setBusy] = useState(false);
  if (!organization) return null;
  const ok = name.trim() === organization.name.trim();

  const run = async () => {
    if (!ok) return;
    try {
      setBusy(true);
      const res = await call({ organizationId: organization.id, confirmName: name.trim(), mode });
      const c = res.data.counters || {};
      const n = Object.entries(c).filter(([k]) => k !== 'usersDetached').reduce((s, [, v]) => s + (v as number), 0);
      toast.success(`Szervezet törölve${n ? ` · ${n} kapcsolódó dokumentum` : ''}${c.usersDetached ? ` · ${c.usersDetached} tag leválasztva` : ''}`);
      onDeleted(organization);
      onClose();
      setName('');
    } catch (e: any) {
      const msg = e?.message?.replace(/^.*?:\s*/, '') || 'A törlés nem sikerült';
      toast.error(msg.includes('not-found') || msg.includes('NOT_FOUND') ? 'A törlő függvény nincs telepítve (firebase deploy --only functions:adminDeleteOrganization)' : msg);
    } finally { setBusy(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Szervezet törlése" size="lg">
      <div className="p-5 space-y-4">
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/20 px-3 py-2.5 text-sm text-rose-800 dark:text-rose-300">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
          <div>A(z) <strong>{organization.name}</strong> szervezet törlése <strong>nem visszavonható</strong>. A tagok elvesztik a hozzáférést, az előfizetés megszűnik.</div>
        </div>
        <div className="space-y-2 text-sm">
          <label className="flex items-start gap-2 cursor-pointer"><input type="radio" className="mt-1" checked={mode === 'cascade'} onChange={() => setMode('cascade')} /><span><span className="font-medium">Minden adatával együtt</span> – ügyfelek, leadek, ajánlatok, megrendelések, feladatok, projektek, raktár, dokumentumok, előfizetés (ajánlott, nem marad árva adat)</span></label>
          <label className="flex items-start gap-2 cursor-pointer"><input type="radio" className="mt-1" checked={mode === 'orgOnly'} onChange={() => setMode('orgOnly')} /><span><span className="font-medium">Csak a szervezet</span> – az adatok megmaradnak az adatbázisban szervezet nélkül (csak ha később vissza akarod kötni)</span></label>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Megerősítésként írd be a szervezet nevét: <span className="font-mono">{organization.name}</span></label>
          <input value={name} onChange={e => setName(e.target.value)} className="w-full h-9 border rounded-lg px-3 text-sm" placeholder={organization.name} autoFocus />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy} className="crm-btn crm-btn-ghost">Mégse</button>
          <button type="button" onClick={run} disabled={!ok || busy} className="crm-btn crm-btn-danger">{busy ? 'Törlés…' : 'Szervezet végleges törlése'}</button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteOrganizationModal;
